<!-- GenericSearch.vue -->
<script lang="ts" setup>
import { computed, onBeforeMount, reactive, watch } from 'vue';
import _ from 'lodash';
import * as choiceApi from '../../lib/ChoiceApi';
import { GenericSearchResult, IGenericSearchResultRow, IResultHeaderInfo, getIdValue } from '../../lib/Api/Search';
import vSelect from 'vue-select';
import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
import { assertInternally } from '../../lib/Environment';
import Icon from '../Icon.vue';
import { DisplayStyle, IconTypes } from '../../Enums/UiEnums';
import { popAlert } from '../../lib/Notification';
import MessageBadge from '../Messages/MessageBadge.vue';

    const props = defineProps<{
        modelValue?: any,
        searchId: string,
        idType?: DatabaseIdTypes,
        idField?: string,
        unselectedStateText?: string
     }>();

    const emit = defineEmits<{
        "update:model-value": any|undefined,
        "selectedOption": [IGenericSearchResultRow|undefined]
    }>();

    const compData = reactive<{
        lastSearchText?: string,
        //options?: IGenericSearchResultRow[],
        searchResult?: GenericSearchResult,
        selectedOption?: IGenericSearchResultRow
    }>({});

    function emitValue(selectedOption?: IGenericSearchResultRow)
    {
        // Set the internal value
        compData.selectedOption = selectedOption;

        // Indicate what the selected option is
        emit("selectedOption", selectedOption);

        // Emit the ID value
        //  If we don't have a selection, emit the and leave
        if (!selectedOption)
        {
            emit("update:model-value", undefined);
            return;
        }

        // Get the id value for the selected opion
        if (props.idField)
        {
            if (!compData.searchResult)
            {
                assertInternally(`compData.searchResult is undefined`);
                emit("update:model-value", undefined);    
                return;
            }

            const idValue = getIdValue(props.idField, selectedOption, compData.searchResult);
            emit("update:model-value", idValue);
        }
        else
        {
            // The id field wasn't specified so assume the first ID column is the keyed column
            const id = selectedOption.id_1;
            emit("update:model-value", id);            
        }
    }

    watch(() => props.modelValue, (_newVal, _oldVal) => {
        if (!options.value)
        {
            onLoadIdValue();
        }
    });

    onBeforeMount(() => {
        onLoadIdValue();
    });

    // Get the option(s) associated with the selected value
    async function onLoadIdValue()
    {
        if (props.modelValue)
        {
            compData.searchResult = await choiceApi.search.fetchById(props.searchId, props.modelValue);
            if (options.value?.length)
            {
                compData.selectedOption = options.value[0];
            }
            else
            {
                compData.selectedOption = undefined;
            }
        }
    }

    // Do NOT call directly!  Use: onDebouncedSearch
    async function onSearch(searchText: string, fnLoading: (isLoading: boolean) => void)
    {
        compData.lastSearchText = searchText;
        fnLoading(true);
        compData.searchResult = await choiceApi.search.genericSearch(props.searchId, searchText, false);
        fnLoading(false);
    }
    const onDebouncedSearch = _.debounce(onSearch, 350);

    // Search for all of the specified text
    async function onSearchAll()
    {
        compData.searchResult = await choiceApi.search.genericSearch(props.searchId, compData.lastSearchText, true);
    }    

    const options = computed<IGenericSearchResultRow[] | undefined>(() => compData.searchResult?.rows);
    const haveMoreResults = computed(() => (compData.searchResult?.info?.totalMatches ?? 0) > (options.value?.length ?? 0));

    function rowColStyle(headerItem: IResultHeaderInfo): string
    {
        return `display:inline-block; width:${headerItem.widthInPx}px`;
    }

    const resultsText = computed<string>(() =>{
        switch(options.value?.length ?? 0)
        {
            case 0:
                return "No results";
            case 1:
                return "1 result";
            default:
                return `${options.value?.length} results`;
        }
    });
</script>

<template>
    <vSelect :modelValue="compData.selectedOption" 
        @update:model-value="val => emitValue(val)"
        :filterable="false"
        :options="options" 
        @search="onDebouncedSearch">
        <template #selected-option="option">
            {{ option ? option.col_1 : unselectedStateText }}
          </template>        
        <template #option="option">
            <slot name="option" :option="option">
                <div v-for="(headerPart, index) in compData.searchResult?.info?.headerInfo" :style="rowColStyle(headerPart)">
                    {{option[`col_${1+index}`]}}
                </div>
            </slot>
        </template>
        <template #no-options="{ search, _searching, _loading }">
            <span v-if="search.length && !options?.length" class="text-danger">
                No match
            </span>
            <span v-if="!search.length">
                Enter search text: {{ search }}
            </span>
        </template>
        <template #list-footer="{ search}">
            <em class="text-muted">
                <div v-if="haveMoreResults" @click="() => onSearchAll()" style="cursor:pointer" title="Click to show remaining results">
                    {{ options?.length }} of 
                    {{ compData.searchResult?.info?.totalMatches }} <Icon :icon="IconTypes.Search" />
                    results
                    <MessageBadge :displayStyle="DisplayStyle.Danger" v-if="options?.length === compData.searchResult?.info?.maxResultRows" title="To reduce the number of results, improve your search filter.">
                        Max results: {{ compData.searchResult?.info?.maxResultRows }}
                    </MessageBadge>
                </div>
                <div v-else>
                    {{ resultsText }}
                </div>
            </em>
        </template>                
    </vSelect>
</template>